/**
 * ServiceCustomersTab — "Service Customers" tab on /customers page.
 * Shows service_leads where the customer has TRANSACTED (PARTIAL_PAID,
 * PAID, or COMPLETED service). Mirrors the layout of the existing
 * Inspection Customers tab: date-range pills + KPI cards + filters +
 * paginated table + slide-over drawer for detail.
 *
 * Added 2026-06-04.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { customersApi, utilityApi } from '@/services/api';
import { formatCurrency } from '@/utils/currency';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Search, Loader2, Filter, ChevronLeft, ChevronRight, MapPin,
  Phone, Mail, Package, User, Calendar, IndianRupee, CheckCircle,
  Clock, Wrench, X, AlertCircle, Car,
} from 'lucide-react';
import { ServiceCustomerDetailModal } from './ServiceCustomerDetailModal';

const DATE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'last_7_days', label: 'Last 7 Days' },
  { key: 'last_14_days', label: 'Last 14 Days' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'year', label: 'This Year' },
  { key: 'all', label: 'All Time' },
];

const getDateRange = (preset) => {
  const today = new Date();
  const iso = (d) => d.toISOString().split('T')[0];
  let from = '', to = '';
  switch (preset) {
    case 'today': from = to = iso(today); break;
    case 'last_7_days': {
      const d = new Date(today); d.setDate(today.getDate() - 6);
      from = iso(d); to = iso(today); break;
    }
    case 'last_14_days': {
      const d = new Date(today); d.setDate(today.getDate() - 13);
      from = iso(d); to = iso(today); break;
    }
    case 'week': {
      const d = new Date(today); d.setDate(today.getDate() - today.getDay());
      from = iso(d); to = iso(today); break;
    }
    case 'month':
      from = iso(new Date(today.getFullYear(), today.getMonth(), 1));
      to = iso(today); break;
    case 'year':
      from = iso(new Date(today.getFullYear(), 0, 1));
      to = iso(today); break;
    default:
      from = ''; to = '';
  }
  return { from, to };
};

const STATUS_STYLE = {
  COMPLETED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  BOOKED: 'bg-blue-100 text-blue-700 border-blue-200',
  CONFIRMED: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  PAYMENT_LINK_SENT: 'bg-amber-100 text-amber-700 border-amber-200',
  NEW: 'bg-slate-100 text-slate-700 border-slate-200',
};
const PAY_STYLE = {
  PAID: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  PARTIAL_PAID: 'bg-blue-100 text-blue-700 border-blue-200',
  LINK_SENT: 'bg-amber-100 text-amber-700 border-amber-200',
  PENDING: 'bg-slate-100 text-slate-700 border-slate-200',
  EXPIRED: 'bg-rose-100 text-rose-700 border-rose-200',
};

const StatusBadge = ({ value, kind = 'status' }) => {
  const cfg = kind === 'pay' ? PAY_STYLE : STATUS_STYLE;
  const cls = cfg[value] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {value || '—'}
    </span>
  );
};

// Plain-English payment progress badge derived from paid amount vs grand total.
// Replaces internal status codes (LINK_SENT, PARTIAL_PAID) with rep-friendly labels.
const PAY_TONE = {
  paid:    'bg-emerald-100 text-emerald-700 border-emerald-200',
  partial: 'bg-blue-100 text-blue-700 border-blue-200',
  pending: 'bg-slate-100 text-slate-700 border-slate-200',
};
const PaymentProgressBadge = ({ label, tone }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${PAY_TONE[tone] || PAY_TONE.pending}`}>
    {label}
  </span>
);

const KpiCard = ({ title, value, icon: Icon, accent, testid }) => (
  <div className="rounded-xl border bg-white p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className={`text-2xl font-bold ${accent || 'text-gray-900'}`}>{value}</p>
      </div>
      <div className={`p-3 rounded-xl ${accent === 'text-emerald-600' ? 'bg-emerald-500' : accent === 'text-blue-600' ? 'bg-blue-500' : accent === 'text-amber-600' ? 'bg-amber-500' : 'bg-purple-500'}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
    </div>
  </div>
);

export const ServiceCustomersTab = ({ countryCode }) => {
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ total: 0, booked: 0, completed: 0, revenue: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPayment, setFilterPayment] = useState('');

  const [dateRangeType, setDateRangeType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Drawer state
  const [drawerId, setDrawerId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (filterCity && filterCity !== 'all') params.city = filterCity;
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      if (filterPayment && filterPayment !== 'all') params.payment_status = filterPayment;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const countParams = { ...params };
      delete countParams.page;
      delete countParams.limit;

      const [listRes, cardsRes, citiesRes] = await Promise.all([
        customersApi.getServiceCustomers(params),
        customersApi.getServiceCustomerCardCounts(countParams),
        utilityApi.getCities(),
      ]);
      setItems(listRes.data?.data || []);
      setTotal(listRes.data?.total || 0);
      setTotalPages(listRes.data?.total_pages || 1);
      setCounts(cardsRes.data || { total: 0, booked: 0, completed: 0, revenue: 0 });
      setCities(citiesRes.data || []);
    } catch (e) {
      toast.error('Failed to load service customers');
    } finally {
      setLoading(false);
    }
  }, [search, filterCity, filterStatus, filterPayment, dateFrom, dateTo, page]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handlePreset = (key) => {
    setDateRangeType(key);
    setPage(1);
    if (key === 'all') { setDateFrom(''); setDateTo(''); return; }
    const r = getDateRange(key);
    setDateFrom(r.from); setDateTo(r.to);
  };

  const handleClearFilters = () => {
    setSearch(''); setFilterCity(''); setFilterStatus(''); setFilterPayment('');
    setDateRangeType('all'); setDateFrom(''); setDateTo(''); setPage(1);
  };

  const fmtSlot = (s) => {
    if (!s) return '—';
    // s is "YYYY-MM-DD HH:mm" or ISO
    try {
      const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
      if (isNaN(d.getTime())) return s;
      return d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return s; }
  };

  return (
    <div className="space-y-6" data-testid="service-customers-tab">
      {/* Date range pills */}
      <div className="bg-white border rounded-xl p-4 flex flex-wrap items-center gap-2" data-testid="service-date-range">
        <Calendar className="h-4 w-4 text-gray-500 mr-2" />
        <span className="text-sm font-medium text-gray-600 mr-2">Date Range:</span>
        {DATE_PRESETS.map(p => (
          <button
            key={p.key}
            onClick={() => handlePreset(p.key)}
            data-testid={`service-date-preset-${p.key}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              dateRangeType === p.key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >{p.label}</button>
        ))}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard title="Total Service Customers" value={counts.total.toLocaleString()} icon={User} accent="text-blue-600" testid="kpi-total" />
        <KpiCard title="Booked / Awaiting" value={counts.booked.toLocaleString()} icon={Clock} accent="text-amber-600" testid="kpi-booked" />
        <KpiCard title="Service Completed" value={counts.completed.toLocaleString()} icon={CheckCircle} accent="text-emerald-600" testid="kpi-completed" />
        <KpiCard title="Total Service Revenue" value={formatCurrency(counts.revenue, countryCode)} icon={IndianRupee} accent="text-purple-600" testid="kpi-revenue" />
      </div>

      {/* Filters bar */}
      <div className="bg-white border rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
          <div className="md:col-span-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              data-testid="service-search-input"
              placeholder="Search name, mobile, email, registration..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10"
            />
          </div>
          <div className="md:col-span-2">
            <Select value={filterCity || 'all'} onValueChange={(v) => { setFilterCity(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="service-city-filter"><SelectValue placeholder="All Cities" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map(c => (
                  <SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Select value={filterStatus || 'all'} onValueChange={(v) => { setFilterStatus(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="service-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="NEW">New</SelectItem>
                <SelectItem value="BOOKED">Booked</SelectItem>
                <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                <SelectItem value="PAYMENT_LINK_SENT">Payment Link Sent</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Select value={filterPayment || 'all'} onValueChange={(v) => { setFilterPayment(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="service-payment-filter"><SelectValue placeholder="All Payment" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payment</SelectItem>
                <SelectItem value="PAID">Paid</SelectItem>
                <SelectItem value="PARTIAL_PAID">Partial Paid</SelectItem>
                <SelectItem value="LINK_SENT">Link Sent</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Button
              variant="outline"
              onClick={handleClearFilters}
              data-testid="service-clear-filters"
              className="w-full"
            >
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]" data-testid="service-customers-table">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Created / Paid</th>
                <th className="px-4 py-3 text-left font-semibold">Customer</th>
                <th className="px-4 py-3 text-left font-semibold">Vehicle</th>
                <th className="px-4 py-3 text-left font-semibold">City</th>
                <th className="px-4 py-3 text-left font-semibold">Package</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Pricing</th>
                <th className="px-4 py-3 text-left font-semibold">Payment</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center">
                  <Loader2 className="h-6 w-6 animate-spin inline-block text-gray-400" />
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  No service customers found for the selected filters
                </td></tr>
              ) : items.map(it => {
                // "Paid at" if customer transacted, else fall back to created_at.
                const paidTs = it.razorpay_paid_at;
                const showPaid = !!paidTs;
                const tsRaw = paidTs || it.created_at;
                const tsDate = tsRaw ? new Date(tsRaw) : null;

                // True final = service package final + spare parts total.
                const sparePartsTotal = Number(it.spare_parts_total || 0);
                const packageFinal = Number(it.final_price || 0);
                const grandTotal = packageFinal + sparePartsTotal;

                // Derive payment-progress label from amounts (not stored status).
                const paidAmount = Number(
                  it.paid_amount !== undefined && it.paid_amount !== null
                    ? it.paid_amount
                    : (it.advance_amount || 0)
                );
                let payLabel = 'Not Paid';
                let payTone = 'pending';
                if (paidAmount > 0) {
                  if (grandTotal > 0 && paidAmount + 0.5 >= grandTotal) {
                    payLabel = 'Fully Paid';
                    payTone = 'paid';
                  } else {
                    payLabel = 'Advance Paid';
                    payTone = 'partial';
                  }
                }

                return (
                <tr
                  key={it.id}
                  onClick={() => setDrawerId(it.id)}
                  data-testid={`service-customer-row-${it.id}`}
                  className="border-t hover:bg-blue-50/40 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    {tsDate && !isNaN(tsDate.getTime()) ? (
                      <div className="space-y-0.5">
                        <div className="text-xs font-medium text-gray-900">
                          {tsDate.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' })}
                        </div>
                        <div className="text-xs text-gray-500">
                          {tsDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        {showPaid && (
                          <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-100 text-emerald-700">
                            paid
                          </span>
                        )}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900">{it.customer_name || '—'}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <Phone className="h-3 w-3" /> {it.mobile || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {it.vehicle ? (
                      <div className="flex items-start gap-1">
                        <Car className="h-3.5 w-3.5 mt-0.5 text-gray-400 shrink-0" />
                        <div className="min-w-0">
                          <div className="font-mono font-semibold text-[12px] text-gray-900 whitespace-nowrap">
                            {it.vehicle.registration || <span className="text-gray-400 italic">No reg #</span>}
                          </div>
                          <div className="text-gray-600 whitespace-nowrap mt-0.5">
                            {[it.vehicle.brand, it.vehicle.model].filter(Boolean).join(' ') || '—'}
                          </div>
                          {it.vehicle.fuel_type && (
                            <div className="text-gray-400 whitespace-nowrap text-[11px]">{it.vehicle.fuel_type}</div>
                          )}
                        </div>
                      </div>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-xs text-gray-700 whitespace-nowrap">
                      <MapPin className="h-3 w-3" /> {it.city || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 min-w-[200px]">
                    <div className="text-sm font-medium text-gray-700">{it.package_name || '—'}</div>
                    {it.package_category && (
                      <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded-full text-xs bg-blue-100 text-blue-700">
                        {it.package_category}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3"><StatusBadge value={it.status} /></td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    <div className="font-semibold text-emerald-700">
                      {formatCurrency(grandTotal, countryCode)}
                    </div>
                    {sparePartsTotal > 0 && (
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        Pkg {formatCurrency(packageFinal, countryCode)}
                        {' + Parts '}
                        {formatCurrency(sparePartsTotal, countryCode)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <PaymentProgressBadge label={payLabel} tone={payTone} />
                    {paidAmount > 0 && (
                      <div className="text-[11px] text-gray-500 mt-1">
                        {formatCurrency(paidAmount, countryCode)} of {formatCurrency(grandTotal, countryCode)}
                      </div>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages} — {total.toLocaleString()} total
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                data-testid="service-prev-page"
              ><ChevronLeft className="h-4 w-4" /> Prev</Button>
              <Button
                variant="outline" size="sm"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                data-testid="service-next-page"
              >Next <ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {drawerId && (
        <ServiceCustomerDetailModal
          serviceLeadId={drawerId}
          isOpen={!!drawerId}
          onClose={() => setDrawerId(null)}
          countryCode={countryCode}
        />
      )}
    </div>
  );
};

export default ServiceCustomersTab;
